import * as React from 'react';
import {useDispatch} from 'react-redux';
import {getDoc, onSnapshot, setDoc} from 'firebase/firestore';
import {useAuth} from './AuthContext';
import {userDoc} from '../firebase/firestore';
import store from '../store/store';
import {Playlist, Song} from '../store/api/types';
import {setAllFavorites} from '../store/slices/favorites/reducers';
import {setAllLikedSongs} from '../store/slices/likedSongs/reducers';
import {setValue as setUserValue} from '../store/slices/user/reducers';

interface UserDataDoc {
    favorites?: Record<string, Playlist>;
    likedSongs?: {songs: Record<string, Song>; order: string[]};
    recentlyPlayed?: Record<string, Playlist>;
    updatedAt?: number;
}

/**
 * Keeps local favorites / liked songs / recently-played in sync with users/{uid}.
 *
 * On sign-in:
 *   1. Fetch the user doc.
 *   2. If the doc is missing AND local has guest data → upload local (one-time migration).
 *   3. Otherwise replace local state with remote (Firestore wins across devices).
 *   4. Subscribe via onSnapshot for live updates.
 *
 * On sign-out: clear local state so the next user starts clean.
 *
 * Outgoing writes are mirrored via userDataSyncMiddleware.
 */
export const FirestoreUserDataSync: React.FC = () => {
    const {user, status} = useAuth();
    const dispatch = useDispatch();
    const prevUidRef = React.useRef<string | null>(null);

    React.useEffect(() => {
        if (status === 'loading') return;

        const uid = user?.uid ?? null;
        const prevUid = prevUidRef.current;
        prevUidRef.current = uid;

        if (!uid) {
            if (prevUid) {
                dispatch(setAllFavorites({}));
                dispatch(setAllLikedSongs({songs: {}, order: []}));
                dispatch(setUserValue({key: 'recentlyPlayed', value: {}}));
            }
            return;
        }

        const ref = userDoc(uid);
        let unsubscribe: (() => void) | null = null;
        let cancelled = false;

        (async () => {
            try {
                const snap = await getDoc(ref);
                if (cancelled) return;

                const local = store.getState();
                const hasLocalData =
                    Object.keys(local.favorites.playlists).length > 0
                    || local.likedSongs.order.length > 0
                    || Object.keys(local.user.recentlyPlayed).length > 0;

                if (!snap.exists() && hasLocalData) {
                    // First-ever sign-in with guest data — upload local.
                    await setDoc(ref, {
                        favorites: local.favorites.playlists,
                        likedSongs: {
                            songs: local.likedSongs.songs,
                            order: local.likedSongs.order,
                        },
                        recentlyPlayed: local.user.recentlyPlayed,
                        updatedAt: Date.now(),
                    });
                    // onSnapshot below will mirror back into Redux.
                } else if (snap.exists()) {
                    applySnapshot(snap.data() as UserDataDoc);
                }

                if (cancelled) return;
                unsubscribe = onSnapshot(ref, s => {
                    if (s.exists()) applySnapshot(s.data() as UserDataDoc);
                });
            } catch (err) {
                console.error('Firestore user-data sync failed', err);
            }
        })();

        function applySnapshot(data: UserDataDoc) {
            dispatch(setAllFavorites(data.favorites ?? {}));
            dispatch(setAllLikedSongs(data.likedSongs ?? {songs: {}, order: []}));
            dispatch(setUserValue({key: 'recentlyPlayed', value: data.recentlyPlayed ?? {}}));
        }

        return () => {
            cancelled = true;
            unsubscribe?.();
        };
    }, [user, status, dispatch]);

    return null;
};
